import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Rule, ScheduleType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Chip,
  ConfirmationModal,
  Table,
  TableColumnConfig,
  emptyStateGraphics,
  useAlert,
  useModalControl,
} from '@frontend/design-system';
import { DirectAvailabilityCheck } from '../../components/DirectAvailabilityCheck';
import {
  DEFAULT_EXCEPTION_FILTER,
  PractitionerExceptionTableFilter,
} from '../../components/PractitionerExceptionTableFilter';
import { ScheduleBanner } from '../../components/ScheduleBanner';
import { scheduleDirectAvailabilityBannerText } from '../../constants';
import { useSchedulingLocationInfo } from '../../hooks';
import { getDateRangeFromRRule } from '../../rrule-helper';
import { ExceptionStatus, ExceptionListItem, ExceptionTableFilter } from '../../types';
import {
  findOverrideIndexById,
  getFormattedExceptionDateRange,
  getFormattedExceptionTimeRange,
  isPastException,
  isUpcomingException,
} from '../../utils';
import { CreateExceptionModal } from './CreateExceptionModal';

const { providerScheduleDirectAvailabilityTitle, providerScheduleDirectAvailabilityMessage } =
  scheduleDirectAvailabilityBannerText;

type ExceptionsV3PageProps = {
  entityId: string;
  locationId: string;
  entityName: string;
  entityType: ScheduleType.PROVIDER | ScheduleType.LOCATION;
};

export const ExceptionsV3Page = ({ entityId, locationId, entityName, entityType }: ExceptionsV3PageProps) => {
  const [selectedFilterValue, setSelectedFilterValue] = useState<ExceptionTableFilter>(DEFAULT_EXCEPTION_FILTER);
  const [selectedException, setSelectedException] = useState<ExceptionListItem>({} as ExceptionListItem);
  const { t } = useTranslation('scheduleGlobalSettings');
  const alert = useAlert();
  const { getLocationTimeZone } = useSchedulingLocationInfo();
  const locationTimeZone = getLocationTimeZone(locationId);

  const entityTypeName = entityType === ScheduleType.PROVIDER ? t('Provider') : t('Office');

  // Custom modal control to reset selected exception on closing modal
  const customModalControl = {
    onClose: () => setSelectedException({} as ExceptionListItem),
  };

  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl(customModalControl);
  const { modalProps: editModalProps, triggerProps: editTriggerProps } = useModalControl(customModalControl);

  const deleteScheduleMutation = SchedulerV3.Mutations.useUpdateScheduleMutation({
    options: {
      onSuccess: () => {
        alert.success(t('{{entityTypeName}} exceptions deleted successfully', { entityTypeName }));
      },
      onError: () => {
        alert.error(t('Failed to delete {{entityTypeName}} exceptions', { entityTypeName }));
      },
    },
  });

  const listSchedulesQuery = SchedulerV3.Queries.useListSchedulesQuery({
    request: {
      locationId,
      ids: [entityId],
    },
    options: {
      enabled: !!locationId && !!entityId,
    },
  });

  const deleteException = () => {
    const entitySchedule = listSchedulesQuery.data?.schedules?.[0];
    if (!entitySchedule) return;

    const { availabilities = [], unAvailabilities = [] } = entitySchedule.recurrenceRules?.override || {};

    const updateOverrides = (overrides: Rule[]) => {
      const overrideIndex = findOverrideIndexById(overrides, selectedException.id);
      if (overrideIndex !== -1) {
        overrides.splice(overrideIndex, 1);
      }
    };

    if (selectedException?.status === ExceptionStatus.Available) {
      updateOverrides(availabilities);
    } else if (selectedException?.status === ExceptionStatus.Unavailable) {
      updateOverrides(unAvailabilities);
    }

    deleteScheduleMutation.mutate({
      id: entitySchedule.id,
      locationId,
      type: entitySchedule.type || entityType,
      recurrenceRules: {
        ...entitySchedule.recurrenceRules,
        override: { availabilities, unAvailabilities },
      },
    });
  };

  const handleDeleteModal = (exception: ExceptionListItem) => {
    setSelectedException(exception);
    deleteTriggerProps.onClick();
  };

  const handleEditModal = (exception: ExceptionListItem) => {
    setSelectedException(exception);
    editTriggerProps.onClick();
  };

  const tableColumns = useMemo(
    (): TableColumnConfig<ExceptionListItem>[] => [
      {
        Header: t('Name of Exception'),
        id: 'exceptionName',
        accessor: (row) => row.name,
      },
      {
        Header: t('Availability'),
        id: 'availability',
        accessor: (row) => row.status,
        cellRenderer: (status: ExceptionStatus) => {
          return <Chip variant={status === ExceptionStatus.Available ? 'primary' : 'warn'}>{status}</Chip>;
        },
      },
      {
        Header: t('Date'),
        id: 'date',
        accessor: (row) => getFormattedExceptionDateRange(row.startDateTime, row.endDateTime),
        minWidth: 320,
        maxWidth: 360,
        startingSortBy: 'asc',
        sortType: (rowA, rowB) => {
          if (
            rowA.original.startDateTime &&
            rowB.original.startDateTime &&
            rowA.original.startDateTime < rowB.original.startDateTime
          ) {
            return -1;
          }
          return 0;
        },
      },
      {
        Header: t('Time'),
        id: 'time',
        accessor: (row) => {
          const timeText = getFormattedExceptionTimeRange(row.startDateTime, row.duration);
          return timeText === 'All Day' ? t('All Day') : timeText;
        },
        disableSortBy: true,
      },
    ],
    []
  );

  const tableActionMenu = [
    {
      Icon: () => <Icon name='edit' />,
      label: t('Edit'),
      onClick: (exception: ExceptionListItem) => handleEditModal(exception),
    },
    {
      Icon: () => <Icon name='trash' />,
      label: t('Delete'),
      onClick: (exception: ExceptionListItem) => handleDeleteModal(exception),
    },
  ];

  // Local list of exceptions from overrides object
  const exceptionList = useMemo(() => {
    const overrides = listSchedulesQuery.data?.schedules?.[0]?.recurrenceRules?.override;
    if (!overrides) return [];

    const list: ExceptionListItem[] = [];

    const createExceptionListItems = (override: Rule, status: ExceptionStatus) => {
      const name = override.name || '';
      const duration = override.duration || 0;
      const id = override.id || '';
      const { startDateTime, endDateTime } = getDateRangeFromRRule(override.rrule || '');

      list.push({ id, name, status, startDateTime, endDateTime, duration });
    };

    const overrideAvailabilities = overrides?.availabilities || [];
    const overrideUnAvailabilities = overrides?.unAvailabilities || [];

    overrideAvailabilities?.forEach((override: Rule) => {
      createExceptionListItems(override, ExceptionStatus.Available);
    });

    overrideUnAvailabilities?.forEach((override: Rule) => {
      createExceptionListItems(override, ExceptionStatus.Unavailable);
    });

    // Default sort by startDatetime descending
    return list.sort((a, b) => (a.startDateTime && b.startDateTime && a.startDateTime < b.startDateTime ? -1 : 1));
  }, [listSchedulesQuery.data]);

  const filteredExceptionList = useMemo(() => {
    return exceptionList.filter((exception: ExceptionListItem) => {
      if (!exception.startDateTime || !exception.endDateTime) return true;

      if (selectedFilterValue === ExceptionTableFilter.Upcoming) {
        return isUpcomingException(dayjs.tz(exception.endDateTime, locationTimeZone).toDate());
      }

      if (selectedFilterValue === ExceptionTableFilter.Past) {
        const isPast = isPastException(dayjs.tz(exception.startDateTime, locationTimeZone).toDate());
        return isPast;
      }

      return true;
    });
  }, [exceptionList, selectedFilterValue]);

  return (
    <div css={{ paddingTop: '36px' }}>
      {entityType === ScheduleType.PROVIDER && (
        <DirectAvailabilityCheck locationId={locationId}>
          <ScheduleBanner
            fullWidth
            title={providerScheduleDirectAvailabilityTitle}
            message={providerScheduleDirectAvailabilityMessage}
          />
        </DirectAvailabilityCheck>
      )}
      <Table
        isPaginated
        isLoading={listSchedulesQuery.isLoading || deleteScheduleMutation.isLoading}
        colConfig={tableColumns}
        data={filteredExceptionList}
        rowActions={{ actions: tableActionMenu }}
        customToolbarRender={() => <PractitionerExceptionTableFilter setValue={setSelectedFilterValue} />}
        tableActions={[
          {
            type: 'button',
            variant: 'primary',
            label: t('New Exception'),
            onClick: () => editTriggerProps.onClick(),
          },
        ]}
        tableStyle={{
          name: 'manage-entity-exceptions-v3-table',
          styles: `margin-bottom: ${theme.spacing(2)};`,
        }}
        emptyStateConfig={{
          type: 'schedule_alerts' as keyof typeof emptyStateGraphics,
          header: '',
          description:
            selectedFilterValue === ExceptionTableFilter.Past
              ? t('Clean slate: No previous schedule exceptions recorded')
              : t('This {{entityTypeName}} is really sticking to their schedule - no exceptions added so far!', {
                  entityTypeName,
                }),
        }}
        wrapperStyle={css`
          .table-toolbar {
            justify-content: space-between;
          }
        `}
      />
      <ConfirmationModal
        {...deleteModalProps}
        title={t('Delete Exception')}
        destructive
        message={t(
          "Are you sure you want to delete {{exceptionName}}? Doing so will remove this exception from {{entityName}}'s schedule, and their availability will be updated accordingly.",
          { entityName: entityName, exceptionName: selectedException?.name ?? '' }
        )}
        confirmLabel={t('Delete')}
        cancelLabel={t('Cancel')}
        onCancel={deleteModalProps.onClose}
        onConfirm={deleteException}
      />
      <CreateExceptionModal
        modalProps={editModalProps}
        locationId={locationId}
        entityId={entityId}
        entitySchedule={listSchedulesQuery.data?.schedules?.[0]}
        exceptionDetails={selectedException}
        entityType={entityType}
      />
    </div>
  );
};
